import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import type { CatalogCoverage as Coverage } from '../services/apiService';

// Shows a publisher how much of what they write about their catalog can
// actually sell against (feed-coverage-visibility).
//
// The rule this component exists to honour: the two causes of a miss are shown
// SEPARATELY, with their own counts and their own action. An image with no
// description and an image we read fine but had nothing to sell against have
// opposite fixes, so a single "missed" number would send the publisher to fix
// the wrong thing.

const card: React.CSSProperties = {
  border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: '1rem', flex: '1 1 15rem',
};

const CatalogCoveragePanel: React.FC = () => {
  const [data, setData] = useState<Coverage | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService.getCatalogCoverage()
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  // A panel that cannot load is simply absent — coverage is context, and a
  // broken box on the dashboard is worse than no box.
  if (failed || !data) return null;

  if (data.status === 'not-computed' || data.status === 'no-data') {
    return (
      <section className="stats-section" data-testid="coverage-section">
        <h2>Catalogue coverage</h2>
        <p data-testid="coverage-pending" style={{ color: 'var(--muted)' }}>
          Once ImgAff has seen enough eligible image requests, this section will show where your connected product feeds cover the subjects readers encounter.
        </p>
      </section>
    );
  }

  const {
    requests = 0, matched = 0, missedNoDescription = 0, missedNoProducts = 0,
    coveragePercent, uncoveredGroups = [], uncoveredRemainder = 0, computedAt,
  } = data;

  return (
    <section className="stats-section" data-testid="coverage-section">
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        Catalogue coverage
        {/* Sets expectations without describing mechanism: some of what is
            counted as uncovered is ours to improve, not theirs to fix. */}
        <span
          data-testid="coverage-help"
          role="img"
          aria-label="About these numbers"
          title="These numbers reflect what we could match today. We're continuously improving our matching, so coverage can rise without you changing anything."
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '1.15rem', height: '1.15rem', borderRadius: '50%',
            border: '1px solid var(--line)', color: 'var(--muted)',
            fontSize: '0.75rem', fontWeight: 700, cursor: 'help', flexShrink: 0,
          }}
        >?</span>
      </h2>

      <p style={{ color: 'var(--muted)', marginBottom: '0.75rem' }}>
        {/* Deliberately "images we were asked about", not "your site": we only
            know about pages the widget actually ran on. */}
        Of the {requests.toLocaleString()} images we were asked about in the last {data.windowDays} days,{' '}
        <strong data-testid="coverage-matched">{matched.toLocaleString()}</strong> found a product
        {coveragePercent != null && (
          <> — <strong data-testid="coverage-percent">{coveragePercent}%</strong></>
        )}
        .
      </p>

      {coveragePercent == null && (
        <p data-testid="coverage-low-volume" style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
          Not enough traffic yet to give a reliable percentage — the counts above are exact.
        </p>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        <div style={card} data-testid="coverage-no-description">
          <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{missedNoDescription.toLocaleString()}</div>
          <div style={{ fontWeight: 600 }}>images we couldn't read</div>
          <div style={{ color: 'var(--muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
            These had no usable description, so nothing could be matched to them.{' '}
            <a href="https://imgaff.com/targeting-guide.html" target="_blank" rel="noopener noreferrer">
              How to describe images
            </a>
          </div>
        </div>

        <div style={card} data-testid="coverage-no-products">
          <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{missedNoProducts.toLocaleString()}</div>
          <div style={{ fontWeight: 600 }}>images without a relevant product match</div>
          <div style={{ color: 'var(--muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
            We read these fine, but your feeds don't carry products that fit them.{' '}
            <a href="https://imgaff.com/feed-format.html" target="_blank" rel="noopener noreferrer">
              About feeds
            </a>
          </div>
        </div>
      </div>

      {uncoveredGroups.length > 0 && (
        <div style={{ marginTop: '1rem' }} data-testid="coverage-groups">
          <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Frequent image subjects not covered by your feeds</div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {uncoveredGroups.map(g => (
              <li key={g.subject} data-testid={`coverage-group-${g.subject}`}>
                {g.subject} — {g.images.toLocaleString()} images
              </li>
            ))}
          </ul>
          {/* Never let a short list read as the whole story. */}
          {uncoveredRemainder > 0 && (
            <p data-testid="coverage-remainder" style={{ color: 'var(--muted)', fontSize: '0.9rem', marginTop: '0.4rem' }}>
              and {uncoveredRemainder.toLocaleString()} more across smaller subjects.
            </p>
          )}
          <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginTop: '0.4rem' }}>
            If these subjects fit your affiliate strategy, a relevant product feed may create more matching opportunities. Add only catalogues you are allowed to use.
          </p>
        </div>
      )}

      {/* THE ONLY WAY OUT OF THIS PANEL USED TO BE DOCUMENTATION. It reports the
          largest actionable number on the dashboard — images we read fine and had
          nothing to sell against — and every link it offered went to imgaff.com:
          the format guide, and how to describe images. Neither is where a
          publisher fixes this. `/feeds` is: its own contract's fourth question is
          "What should I add next?", answered with an ordered list and the reasons
          served verbatim.

          One link, not one per card. It renders on the COUNT rather than inside
          the subject list, because a publisher can have unmatched images with no
          group breakdown at all — and that publisher needs the way out most.
          The two documentation links stay: "how do I describe an image" and "what
          shape must a feed be" are real questions, and different ones. */}
      {missedNoProducts > 0 && (
        <p style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          <Link to="/feeds/add" data-testid="coverage-feeds-cta">See what to add next →</Link>
        </p>
      )}

      {computedAt && (
        <p data-testid="coverage-computed-at" style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: '0.75rem' }}>
          Updated {new Date(computedAt).toLocaleDateString()}
        </p>
      )}
    </section>
  );
};

export default CatalogCoveragePanel;
